/**
 * SpecVersionHistory Component
 *
 * Displays a vertical timeline of version snapshots for a specification.
 * Supports expanding entries to preview content and highlights the current version.
 *
 * Part of PowerSpec V3 - Spec Version History
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState } from 'react';
import { Card, Badge, Button, Timeline } from 'flowbite-react';
import {
  HiClock,
  HiUser,
  HiDocumentText,
  HiChevronDown,
  HiChevronUp
} from 'react-icons/hi';

export interface VersionRecord {
  version_id: string;
  version: number;
  change_type: string;
  change_summary: string | null;
  changed_by: string;
  created_at: string;
  content_preview?: string | null;
}

interface SpecVersionHistoryProps {
  specId: string;
  versions: VersionRecord[];
  currentVersion: number;
}

const changeTypeBadgeColor = (changeType: string): 'gray' | 'info' | 'warning' | 'success' | 'failure' => {
  switch (changeType) {
    case 'create':
      return 'success';
    case 'update':
      return 'info';
    case 'status_change':
      return 'warning';
    case 'approval':
      return 'success';
    default:
      return 'gray';
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatChangeType = (changeType: string): string => {
  return changeType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const SpecVersionHistory: React.FC<SpecVersionHistoryProps> = ({
  specId,
  versions,
  currentVersion
}) => {
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);

  const toggleExpand = (versionId: string) => {
    setExpandedVersionId((prev) => (prev === versionId ? null : versionId));
  };

  // Sort versions newest first
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  if (sortedVersions.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-8 text-gray-500">
          <HiDocumentText className="w-12 h-12 mb-3 text-gray-300" />
          <p className="text-sm">No version history available for this specification.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
        <p className="text-sm text-gray-500">
          {sortedVersions.length} version{sortedVersions.length !== 1 ? 's' : ''} recorded
        </p>
      </div>

      <Timeline>
        {sortedVersions.map((version) => {
          const isCurrent = version.version === currentVersion;
          const isExpanded = expandedVersionId === version.version_id;

          return (
            <Timeline.Item key={version.version_id}>
              <Timeline.Point icon={HiClock} />
              <Timeline.Content>
                <div
                  className={`p-3 rounded-lg border ${
                    isCurrent
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  } transition-colors`}
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge color={isCurrent ? 'blue' : 'gray'} size="sm">
                        v{version.version}
                      </Badge>
                      <Badge color={changeTypeBadgeColor(version.change_type)} size="sm">
                        {formatChangeType(version.change_type)}
                      </Badge>
                      {isCurrent && (
                        <Badge color="success" size="sm">
                          Current
                        </Badge>
                      )}
                    </div>
                    <Timeline.Time>
                      {formatDate(version.created_at)}
                    </Timeline.Time>
                  </div>

                  {/* Change Summary */}
                  {version.change_summary && (
                    <p className="text-sm text-gray-700 mb-2">
                      {version.change_summary}
                    </p>
                  )}

                  {/* Changed By */}
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                    <HiUser className="w-3 h-3" />
                    <span>{version.changed_by}</span>
                  </div>

                  {/* Expand/Collapse for Content Preview */}
                  {version.content_preview !== undefined && (
                    <div>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => toggleExpand(version.version_id)}
                        className="mt-1"
                      >
                        {isExpanded ? (
                          <>
                            <HiChevronUp className="w-3 h-3 mr-1" />
                            Hide Preview
                          </>
                        ) : (
                          <>
                            <HiChevronDown className="w-3 h-3 mr-1" />
                            Show Preview
                          </>
                        )}
                      </Button>

                      {isExpanded && version.content_preview && (
                        <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-700 max-h-60 overflow-y-auto">
                          <pre className="whitespace-pre-wrap font-sans">
                            {version.content_preview}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Timeline.Content>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </Card>
  );
};

export default SpecVersionHistory;
